import { useState } from "react";
import { Mic, Upload, Trash2, ExternalLink, Play, Pause, Rss, Copy } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const PodcastTab = ({ token, currentUser }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', published: true });
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [audioDragOver, setAudioDragOver] = useState(false);

  const userId = currentUser?.user_id || currentUser?.id;
  const rssUrl = `${BACKEND_URL}/api/news/podcast/rss/${userId}`;

  useState(() => {
    fetchEpisodes();
  }, []);

  async function fetchEpisodes() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/podcast/episodes`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setEpisodes(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      let audioUrl = '';
      if (audioFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append('file', audioFile);
        const uploadRes = await fetch(`${BACKEND_URL}/api/news/podcast/upload-audio`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        });
        if (uploadRes.ok) {
          const data = await uploadRes.json();
          audioUrl = data.audio_url;
        }
        setUploading(false);
      }
      const res = await fetch(`${BACKEND_URL}/api/news/podcast/episodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, audio_url: audioUrl }),
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ title: '', description: '', published: true });
        setAudioFile(null);
        fetchEpisodes();
      }
    } catch (err) { console.error(err); }
    finally { setSaving(false); setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this episode?')) return;
    await fetch(`${BACKEND_URL}/api/news/podcast/episodes/${id}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
    });
    fetchEpisodes();
  };

  const copyRss = () => {
    navigator.clipboard.writeText(rssUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-white/20 border-t-purple-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="podcast-tab">
      {/* RSS Feed Link */}
      <div className="bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/20 rounded-2xl p-5 flex items-center justify-between" data-testid="podcast-rss-bar">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Rss className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <p className="text-white font-medium text-sm">Your Podcast RSS Feed</p>
            <p className="text-white/40 text-xs truncate max-w-sm">{rssUrl}</p>
          </div>
        </div>
        <button onClick={copyRss}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${copied ? 'bg-green-500/20 text-green-400' : 'bg-white/10 hover:bg-white/15 text-white/70'}`}
          data-testid="copy-rss-btn">
          <Copy className="w-3.5 h-3.5" /> {copied ? 'Copied!' : 'Copy RSS'}
        </button>
      </div>
      <p className="text-white/30 text-xs -mt-3">Submit this URL to Spotify for Podcasters, Apple Podcasts, or any podcast directory.</p>

      {/* Header + Add */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold text-lg">Episodes ({episodes.length})</h3>
        <button onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg font-medium transition flex items-center gap-1.5"
          data-testid="add-episode-btn">
          <Mic className="w-4 h-4" /> Add Episode
        </button>
      </div>

      {/* Add Episode Form */}
      {showForm && (
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 space-y-4" data-testid="episode-form">
          <div>
            <label className="block text-white/60 text-sm mb-1">Episode Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
              placeholder="Show name - Episode title" data-testid="episode-title" />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition resize-none"
              placeholder="Episode description..." data-testid="episode-description" />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Audio File</label>
            <label
              className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl px-4 py-8 cursor-pointer transition ${audioDragOver ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 bg-white/[0.02] hover:border-purple-500/50'}`}
              data-testid="episode-audio-upload"
              onDragOver={(e) => { e.preventDefault(); setAudioDragOver(true); }}
              onDragLeave={() => setAudioDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setAudioDragOver(false); if (e.dataTransfer.files[0]) setAudioFile(e.dataTransfer.files[0]); }}
            >
              <Upload className={`w-8 h-8 mb-2 ${audioDragOver ? 'text-purple-400' : 'text-white/20'}`} />
              {audioFile ? (
                <div className="text-center">
                  <p className="text-white text-sm font-medium">{audioFile.name}</p>
                  <p className="text-white/40 text-xs mt-1">{(audioFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-white/50 text-sm">Drag & drop your audio file here</p>
                  <p className="text-white/30 text-xs mt-1">or click to browse — MP3, WAV, OGG</p>
                </div>
              )}
              <input type="file" accept="audio/*" className="hidden" onChange={(e) => { if (e.target.files[0]) setAudioFile(e.target.files[0]); }} />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-white/60 text-sm cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="rounded border-white/20" /> Publish immediately (include in RSS)
            </label>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving || !form.title || !audioFile}
              className="px-6 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white rounded-xl font-medium text-sm transition"
              data-testid="episode-save-btn">
              {uploading ? 'Uploading audio...' : saving ? 'Saving...' : 'Save Episode'}
            </button>
            <button onClick={() => { setShowForm(false); setForm({ title: '', description: '', published: true }); setAudioFile(null); }}
              className="px-6 py-2.5 bg-white/10 hover:bg-white/15 text-white/70 rounded-xl text-sm transition">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Episodes List */}
      {episodes.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
          <Mic className="w-12 h-12 text-white/10 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No episodes yet. Upload your first recorded show!</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="episodes-list">
          {episodes.map((ep) => (
            <div key={ep.episode_id} className="flex items-center gap-4 bg-white/[0.03] border border-white/[0.08] rounded-xl p-4" data-testid={`episode-${ep.episode_id}`}>
              <button onClick={() => {
                if (playingId === ep.episode_id) { document.getElementById(`audio-${ep.episode_id}`)?.pause(); setPlayingId(null); }
                else { document.querySelectorAll('audio').forEach(a => a.pause()); document.getElementById(`audio-${ep.episode_id}`)?.play(); setPlayingId(ep.episode_id); }
              }} className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 hover:bg-purple-500/30 transition">
                {playingId === ep.episode_id ? <Pause className="w-4 h-4 text-purple-400" /> : <Play className="w-4 h-4 text-purple-400 ml-0.5" />}
              </button>
              <audio id={`audio-${ep.episode_id}`} src={ep.audio_url} onEnded={() => setPlayingId(null)} />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{ep.title}</p>
                <p className="text-white/30 text-xs">{new Date(ep.created_at).toLocaleDateString()} {ep.published ? '• Published' : '• Draft'}</p>
              </div>
              <button onClick={() => handleDelete(ep.episode_id)}
                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition flex-shrink-0"
                data-testid={`episode-delete-${ep.episode_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
